import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import type { CampingSpot } from '../types';

/**
 * Planlegg en «vil du sjekke inn?»-lokalvarsling for en plass brukeren har
 * oppholdt seg ved en stund. Trykk på varselet håndteres av App.tsx-lytteren
 * (`extra.action === 'check_in'`), som åpner innsjekk-banneret / AA-prompten.
 *
 * Skilt ut fra proximity-logikken slik at `spotProximity.ts` forblir
 * Capacitor-fri og testbar; her injiseres denne som `notify`.
 */
export function scheduleProximityCheckInNotification(spot: CampingSpot): void {
  const title = 'Vil du sjekke inn?';
  const body = `Du har vært ved ${spot.name || 'en plass'} en stund. Sjekk inn nå!`;

  if (Capacitor.isNativePlatform()) {
    LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: Math.floor(Date.now() / 1000),
          schedule: { at: new Date(Date.now() + 1000) },
          extra: { action: 'check_in', spotId: spot.id },
        },
      ],
    }).catch(err => console.warn('LocalNotifications schedule error:', err));
    return;
  }

  // Web/PWA: IKKE bruk Capacitor-webskallet — det konstruerer `new Notification()`
  // fra en setTimeout, som kaster «Illegal constructor» på Android Chrome (mobil-web
  // tillater kun varsler via service worker-en). Feilen fyres utenfor .catch()-kjeden
  // og havner som uhåndtert feil i error_reports. Vis i stedet via service worker-en,
  // som fungerer både på desktop og Android Chrome, og kun når tillatelse er gitt.
  showWebProximityNotification(title, body, spot.id);
}

/**
 * Web-varsel via ServiceWorkerRegistration.showNotification() — den eneste
 * varslings-API-en som fungerer på Android Chrome. Stille no-op når tillatelse
 * mangler eller service worker ikke er tilgjengelig.
 */
function showWebProximityNotification(title: string, body: string, spotId: string): void {
  try {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.ready
      .then(reg =>
        reg.showNotification(title, {
          body,
          icon: '/favicon-192.png',
          badge: '/favicon-192.png',
          tag: `checkin-${spotId}`,
          data: { action: 'check_in', spotId },
        }),
      )
      .catch(err => console.warn('SW showNotification error:', err));
  } catch (err) {
    console.warn('Web proximity notification error:', err);
  }
}
